import { useSyncExternalStore } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@ezhire/types';
import { useSupabaseClient } from './use-supabase';

/**
 * Feature flag resolution shape, matching public.feature_flags row.
 */
export interface FeatureFlagRecord {
  key: string;
  enabled_globally: boolean;
  enabled_for_users: string[];
  enabled_for_roles: string[];
}

/**
 * Caller identity for flag resolution. Passed by the shell via context
 * or directly to the hook; we don't hardcode dependency on apps/web's
 * auth store so modules can consume this too.
 */
export interface FeatureFlagActor {
  userId: string | null;
  /**
   * The caller's TIERS — `super_admin | admin | editor | viewer`.
   *
   * Named `roles` for historical reasons, which is the whole of this bug: `feature_flags.
   * enabled_for_roles` is populated with role KEYS (`lifecycle_lead`, `call_center_lead`), and
   * matching keys against tiers can never succeed. Six production flags target `lifecycle_lead`
   * today; every one is masked only by `enabled_globally = true`, so the day someone turns global
   * off to narrow a rollout to leads, the feature disappears for everyone with no error anywhere.
   */
  roles: string[];
  /**
   * The caller's role KEYS, checked alongside `roles` rather than instead of it — an allowlist
   * may legitimately name either vocabulary, and silently accepting only one is what made this
   * fail quietly.
   *
   * REQUIRED, deliberately. Optional was the first shape of this fix, and Codex was right to
   * reject it: a silently-omitted field is precisely the defect being repaired, and making it
   * optional would leave the next caller on the broken path with the compiler saying nothing.
   * Callers with no keys pass `[]`.
   */
  roleKeys: string[];
}

/** Shell identity shared with independently packaged modules. */
export interface AtlasActor extends FeatureFlagActor {
  orgId: string | null;
}

/**
 * Singleton subscription state — one realtime channel for the whole
 * feature_flags table regardless of how many hooks mount. Guard against
 * HMR double-init via a globalThis sentinel.
 */
/**
 * Whether the flag table has been read yet, and whether the read worked.
 *
 * `useFeatureFlag` returns a bare boolean, which collapses THREE different situations into
 * `false`: not loaded yet, the read failed, and the flag is genuinely off. Consumers then render
 * "this feature is switched off — go and toggle it", which is a confident and often wrong
 * diagnosis of a network failure, and told an operator to go flip a flag that was already on.
 * Keeping the status alongside the flags lets a gate tell those three apart.
 */
export type FlagLoadStatus = 'loading' | 'ready' | 'error';

interface FlagStoreState {
  client: SupabaseClient<Database> | null;
  flags: Map<string, FeatureFlagRecord>;
  listeners: Set<() => void>;
  channel: ReturnType<SupabaseClient['channel']> | null;
  initPromise: Promise<void> | null;
  refCount: number;
  status: FlagLoadStatus;
  /** Message from the failed read, for a gate to show instead of inventing a cause. */
  error: string | null;
  /**
   * Bumped by teardown. A read captures it and discards its own result if it no longer matches.
   *
   * Without this a read in flight when the last consumer unmounts still lands: it writes flags
   * into a store that was just cleared, opens a realtime channel nothing will ever close, and can
   * stamp `ready` or `error` over a FRESH read started by a remount — leaving a status describing
   * a request from a previous page, and a `retry()` that starts a third read alongside it.
   */
  epoch: number;
}

const GLOBAL_KEY = '__ATLAS_FEATURE_FLAGS__';
type WithGlobal = typeof globalThis & { [GLOBAL_KEY]?: FlagStoreState };

function getStore(): FlagStoreState {
  const g = globalThis as WithGlobal;
  if (!g[GLOBAL_KEY]) {
    g[GLOBAL_KEY] = {
      client: null,
      flags: new Map(),
      listeners: new Set(),
      channel: null,
      initPromise: null,
      refCount: 0,
      status: 'loading',
      error: null,
      epoch: 0,
    };
  }
  return g[GLOBAL_KEY]!;
}

function notify() {
  const store = getStore();
  for (const listener of store.listeners) listener();
}

function applyRow(row: unknown): void {
  if (!row || typeof row !== 'object') return;
  const r = row as Partial<FeatureFlagRecord> & { key?: string };
  if (!r.key) return;
  const store = getStore();
  store.flags.set(r.key, {
    key: r.key,
    enabled_globally: Boolean(r.enabled_globally),
    enabled_for_users: Array.isArray(r.enabled_for_users) ? r.enabled_for_users : [],
    enabled_for_roles: Array.isArray(r.enabled_for_roles) ? r.enabled_for_roles : [],
  });
}

async function initStore(client: SupabaseClient<Database>): Promise<void> {
  const store = getStore();
  if (store.initPromise) return store.initPromise;
  store.client = client;
  const epoch = store.epoch;
  const stale = () => getStore().epoch !== epoch;
  store.initPromise = (async () => {
    const { data, error } = await client
      .from('feature_flags')
      .select('key, enabled_globally, enabled_for_users, enabled_for_roles');
    if (stale()) return; // this read belongs to a torn-down store; its answer is about nothing
    if (error || !data) {
      // The read failed. Two things used to go wrong here and both are fixed.
      //
      // The error was swallowed: the store stayed empty, every flag resolved to `false`, and the
      // gated pages announced "switched off — toggle it in /control/feature-flags" about features
      // that were on. Recording the status lets a gate say "could not check" instead of naming a
      // cause it does not know.
      //
      // And it was PERMANENT: `initPromise` is set before the await and was never cleared, so
      // `initStore` never ran again for the life of the page. One failed request pinned every
      // flag off until a reload.
      //
      // The obvious fix — clear `initPromise` here so the next render retries — was written
      // first and is wrong. `useFeatureFlag` calls `initStore` during RENDER whenever
      // `initPromise` is null, so with it cleared every re-render that happens while the backend
      // is down issues another read. Measured on a quiet page: 2 reads in 6s against 1, and the
      // only reason it is not far worse is that the snapshot cache below keeps the errored state
      // referentially stable, so `notify()` does not itself force a re-render. That bound is a
      // side effect of an unrelated optimisation, which is not something to rely on — any route
      // change or parent re-render would fire another.
      //
      // So the promise STAYS resolved and nothing retries automatically. Recovery is `retry()`,
      // driven by a person clicking a button: one read per click, and a rate no failure can
      // multiply.
      store.status = 'error';
      store.error = error?.message ?? 'the feature flag list came back empty';
      notify();
      return;
    }
    for (const row of data) applyRow(row);
    store.status = 'ready';
    store.error = null;
    // Subscribe once for the whole app. Channel name deterministic.
    if (!store.channel) {
      const channel = client
        .channel('atlas_feature_flags_realtime')
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'feature_flags' },
          (payload) => {
            const eventType = payload.eventType;
            if (eventType === 'DELETE') {
              const old = payload.old as { key?: string };
              if (old.key) store.flags.delete(old.key);
            } else {
              applyRow(payload.new);
            }
            notify();
          },
        )
        .subscribe();
      store.channel = channel;
    }
    notify();
  })();
  return store.initPromise;
}

function teardownIfIdle(): void {
  const store = getStore();
  if (store.refCount > 0) return;
  if (store.channel && store.client) {
    void store.client.removeChannel(store.channel);
    store.channel = null;
  }
  store.initPromise = null;
  store.flags.clear();
  store.status = 'loading';
  store.error = null;
  store.epoch += 1;
}

function subscribe(listener: () => void): () => void {
  const store = getStore();
  store.listeners.add(listener);
  store.refCount += 1;
  // Starting the read is tied to a CONSUMER EXISTING, not to a render happening.
  //
  // The hooks kick `initStore` off during render when `initPromise` is null, which silently
  // assumes a render always follows the store being emptied. It does not: `teardownIfIdle` runs
  // when the last consumer unmounts and clears `initPromise`, and on a route change React can
  // mount the next page's consumer BEFORE that teardown. The new consumer renders while a read is
  // still in flight, so it starts nothing; teardown then nulls the promise; and no further render
  // is scheduled because nothing changed. Observed exactly that: one subscriber, no read in
  // flight, no flags, and a page skeleton that never resolved.
  //
  // `subscribe` runs after every mount, so it is the one hook that cannot be skipped by a
  // component that renders once.
  if (!store.initPromise && store.client) void initStore(store.client);
  return () => {
    store.listeners.delete(listener);
    store.refCount -= 1;
    if (store.refCount <= 0) teardownIfIdle();
  };
}

/**
 * Resolve a flag for a given actor. Pure — safe to call from render.
 */
export function resolveFeatureFlag(
  record: FeatureFlagRecord | undefined,
  actor: FeatureFlagActor,
): boolean {
  if (!record) return false;
  if (record.enabled_globally) return true;
  if (actor.userId && record.enabled_for_users.includes(actor.userId)) return true;
  // Both vocabularies. `enabled_for_roles` is a free-text array an operator fills in, and in
  // production it contains a mix — `super_admin` (a tier) beside `lifecycle_lead` (a key) in the
  // SAME flag. Checking only tiers silently honoured half of such a list.
  if (actor.roles.some((r) => record.enabled_for_roles.includes(r))) return true;
  if (actor.roleKeys?.some((k) => record.enabled_for_roles.includes(k))) return true;
  return false;
}

export interface UseFeatureFlagOptions {
  actor: FeatureFlagActor;
}

/**
 * Subscribe to a feature flag. Returns the resolved boolean for the given
 * actor. Re-renders on live flip. Dedups the realtime subscription at the
 * module level — mounting this hook 50× with the same key opens one
 * channel total.
 */
export function useFeatureFlag(key: string, opts: UseFeatureFlagOptions): boolean {
  const client = useSupabaseClient();
  const store = getStore();
  if (!store.initPromise) {
    void initStore(client);
  }
  const snapshot = useSyncExternalStore(
    subscribe,
    () => {
      const record = store.flags.get(key);
      return resolveFeatureFlag(record, opts.actor);
    },
    () => false, // server snapshot — flags default to off on SSR/pre-hydration
  );
  return snapshot;
}

export interface FeatureFlagState {
  /** Resolved for this actor. Meaningful only when `status` is 'ready'. */
  enabled: boolean;
  status: FlagLoadStatus;
  /** The read's own message when `status` is 'error'. Never a guess at the cause. */
  error: string | null;
  /** Try the read again. No-op while one is already in flight. */
  retry: () => void;
}

/**
 * A feature flag together with whether we actually know its value.
 *
 * Use this wherever a false flag causes something to be HIDDEN and EXPLAINED. `useFeatureFlag`
 * answers "is it on", and answers `false` when it does not know — which is how a failed network
 * read came to be reported to an operator as "this feature is switched off, go and toggle it in
 * /control/feature-flags", about a flag that was already on. That message is a diagnosis, and a
 * diagnosis must not be made from an unread value.
 *
 * The boolean hook is unchanged and still correct where a false flag simply hides a widget.
 */
export function useFeatureFlagState(key: string, opts: UseFeatureFlagOptions): FeatureFlagState {
  const client = useSupabaseClient();
  const store = getStore();
  if (!store.initPromise) {
    void initStore(client);
  }
  // One snapshot covering value AND status: reading them through two `useSyncExternalStore` calls
  // could tear across a notify, and "ready but empty" vs "loading" is exactly the distinction
  // this exists to make. Cached so the snapshot is referentially stable between notifications —
  // useSyncExternalStore re-renders forever if it returns a fresh object each call.
  return useSyncExternalStore(
    subscribe,
    () => snapshotFor(key, opts.actor),
    () => LOADING_SNAPSHOT,
  );
}

/** Server/pre-hydration snapshot. Never 'ready', so nothing announces a flag state before a read. */
const LOADING_SNAPSHOT: FeatureFlagState = {
  enabled: false,
  status: 'loading',
  error: null,
  retry: () => {},
};

const snapshotCache = new Map<string, { deps: string; value: FeatureFlagState }>();

function snapshotFor(key: string, actor: FeatureFlagActor): FeatureFlagState {
  const store = getStore();
  const record = store.flags.get(key);
  const enabled = resolveFeatureFlag(record, actor);
  // Keyed by flag AND actor. The cache held one entry per flag, and the same flag resolved for
  // two different actors then overwrote each other's entry on every read — so
  // `useSyncExternalStore` saw a fresh object each time it checked, with nothing having changed,
  // and re-rendered in a loop. Today the shell supplies one actor, so this could not fire; it is
  // a trap set for whoever renders a flag for someone other than the current user, and the fix
  // is a wider key rather than a comment warning them off.
  // roleKeys is part of the identity because it CHANGES RESOLUTION (Codex, P1-26). Without it,
  // two actors with the same user and tiers but different role keys collide on one entry and
  // hand each other the wrong answer — the same trap the tier key was widened for.
  const cacheKey =
    `${key}\u0000${actor.userId ?? ''}\u0000${actor.roles.join(',')}` +
    `\u0000${(actor.roleKeys ?? []).join(',')}`;
  // Identity is derived from what a consumer can observe, so an unrelated flag flipping does not
  // hand every gate a new object and re-render the app.
  const deps = `${enabled}|${store.status}|${store.error ?? ''}`;
  const hit = snapshotCache.get(cacheKey);
  if (hit && hit.deps === deps) return hit.value;
  const value: FeatureFlagState = {
    enabled,
    status: store.status,
    error: store.error,
    retry: () => {
      const s = getStore();
      if (!s.client || s.status === 'loading') return; // already in flight; it will notify itself
      s.status = 'loading';
      s.error = null;
      // Released ONLY here. `initStore` reassigns it synchronously before its first await, so by
      // the time `notify` re-renders anyone the guard is closed again and no render can start a
      // second read.
      s.initPromise = null;
      void initStore(s.client);
      notify();
    },
  };
  snapshotCache.set(cacheKey, { deps, value });
  return value;
}

/**
 * Get the raw record for a flag. Useful in admin UIs where the whole
 * row needs to be shown. Re-renders on live changes.
 */
export function useFeatureFlagRecord(key: string): FeatureFlagRecord | undefined {
  const client = useSupabaseClient();
  const store = getStore();
  if (!store.initPromise) {
    void initStore(client);
  }
  return useSyncExternalStore(
    subscribe,
    () => store.flags.get(key),
    () => undefined,
  );
}

/**
 * Actor bridge — shell-managed. The apps/web shell calls
 * `setAtlasActor({ userId, roles })` whenever the auth store changes;
 * modules consume via `useAtlasActor()`. Avoids cross-package imports
 * (modules/* cannot import from apps/web/*).
 */
interface ActorStoreState {
  actor: AtlasActor;
  listeners: Set<() => void>;
}
const ACTOR_GLOBAL_KEY = '__ATLAS_ACTOR__';
type WithActorGlobal = typeof globalThis & { [ACTOR_GLOBAL_KEY]?: ActorStoreState };

function getActorStore(): ActorStoreState {
  const g = globalThis as WithActorGlobal;
  if (!g[ACTOR_GLOBAL_KEY]) {
    g[ACTOR_GLOBAL_KEY] = {
      actor: { userId: null, roles: [], roleKeys: [], orgId: null },
      listeners: new Set(),
    };
  }
  return g[ACTOR_GLOBAL_KEY]!;
}

export function setAtlasActor(actor: AtlasActor): void {
  const store = getActorStore();
  store.actor = actor;
  for (const listener of store.listeners) listener();
}

export function useAtlasActor(): AtlasActor {
  const store = getActorStore();
  return useSyncExternalStore(
    (listener) => {
      store.listeners.add(listener);
      return () => { store.listeners.delete(listener); };
    },
    () => store.actor,
    () => ({ userId: null, roles: [], roleKeys: [], orgId: null }),
  );
}
